// Core
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Login from 'components/modals/Login';
import { hasSubscription, isLoggedIn, loadStoredUser } from 'utils/auth';

// Guards a route behind login. With `forUnsubscribed`, also turns away users
// who already have an active subscription (used on the payment pages).
const RequireAuth = ({ children, forUnsubscribed, fallback }) => {
  const { userDetails } = useSelector((store) => store.commonData);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loggedIn = isLoggedIn(userDetails);
  const [showLogin, setShowLogin] = useState(!loggedIn);

  useEffect(() => {
    setShowLogin(!loggedIn);
  }, [loggedIn]);

  // Login calls onClose on success and on cancel; storage tells the two apart.
  const handleCloseLogin = () => {
    setShowLogin(false);
    if (!isLoggedIn(loadStoredUser())) navigate(fallback, { replace: true });
  };

  if (!loggedIn) {
    return (
      <div className='container __innerTopPad text-center'>
        <h2 className='__common-head'>{t('login')}</h2>
        <p>{t('login_with_email')}</p>
        <Login open={showLogin} onClose={handleCloseLogin} />
      </div>
    );
  }

  if (forUnsubscribed && hasSubscription(userDetails)) {
    return <Navigate to='/settings' replace />;
  }

  return children;
};

RequireAuth.propTypes = {
  children: PropTypes.node.isRequired,
  forUnsubscribed: PropTypes.bool,
  fallback: PropTypes.string,
};

RequireAuth.defaultProps = {
  forUnsubscribed: false,
  fallback: '/home',
};

export default RequireAuth;
